import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { debounce, isEqual } from "lodash-es";
import { CACHE_DURATION } from "@consts";
import { gqlClient } from "src/services/graphql";
import { queryClient } from "src/lib/react-query/queryClient";
import useAppStore from "src/stores/appStore";
import { FETCH_QUERY_KEYS } from "src/queries/consts";
import { KNOWLEDGE_FILTER_OPERATORS } from "src/components/collections/knowledge-sources/explore/knowledgeFilterMap";
import { buildStatusPks, isIdField } from "src/components/collections/knowledge-sources/explore/ExploreTab.utils";
import { knowledgeValidationsSelector, validationResultsSelector } from "src/stores/selectors/collectionsSelectors";
import {
  INTROSPECT_DOC,
  toGqlTypeName,
  toUpdateInputTypeName,
  toCreateInputTypeName,
  buildBulkDeleteMutation,
  buildBulkReplaceMutation,
  buildBulkUpsertMutation,
  buildCreateMutation,
  buildDeleteMutation,
  buildExploreQuery,
  buildFieldValuesQuery,
  buildUpdateMutation,
  buildVersionsQuery,
  extractMutationAffectsConfigurationValidations,
  extractMutationValidationScopes,
  splitStatusField
} from "src/queries/services/knowledgeExplore";

// The GraphQL endpoint scopes knowledge by the selected account/assistant via request
// headers, not query variables — so the assistant identity must be part of every explore
// cache key. Otherwise switching assistants serves the previous one's schema/versions/rows.
const useExploreScope = () => [
  useAppStore((s) => s.selectedAccountId),
  useAppStore((s) => s.selectedAssistant?.assistantId)
];

const getExploreScope = () => {
  const { selectedAccountId, selectedAssistant } = useAppStore.getState();
  return [selectedAccountId, selectedAssistant?.assistantId];
};

// Shared between the hooks and the imperative fetch* variants below so both hit
// the same cache entries.
const introspectionQueryOptions = (scope, schemaType) => ({
  queryKey: [FETCH_QUERY_KEYS.EXPLORE_INTROSPECTION, ...scope, schemaType],
  queryFn: async () =>
    splitStatusField(await gqlClient.request(INTROSPECT_DOC, {
      name: toGqlTypeName(schemaType),
      updateInputName: toUpdateInputTypeName(schemaType),
      createInputName: toCreateInputTypeName(schemaType)
    })),
  staleTime: Infinity,
  refetchOnWindowFocus: false
});

const versionsQueryOptions = (scope, schemaType) => ({
  queryKey: [FETCH_QUERY_KEYS.EXPLORE_VERSIONS, ...scope, schemaType],
  queryFn: async () => await gqlClient.request(buildVersionsQuery(schemaType)),
  staleTime: CACHE_DURATION.ONE_MINUTE,
  refetchOnWindowFocus: false
});

// Not in the query key: a validation change refreshes rows in place instead of re-sorting the page.
const readStatusPks = (schemaType, specKey) => {
  const state = useAppStore.getState();
  const { invalidPks, unassignedPks } = buildStatusPks(
    knowledgeValidationsSelector(state)?.[schemaType],
    validationResultsSelector(state),
    specKey
  );
  return { invalid_pks: invalidPks, unassigned_pks: unassignedPks };
};

const MAX_STATUS_ATTEMPTS = 3;

// Resent if the pks change while the request is out, so the page doesn't settle on old ones.
const requestExploreData = ({ schemaType, version, fields, filters, pagination, sort = null, priorityPks = null, status = null }) => {
  const hasPriority = Boolean(priorityPks?.length);
  const query = buildExploreQuery({ schemaType, fields, withSort: Boolean(sort), withPriority: hasPriority, withStatus: Boolean(status) });
  const send = (statusPks) => gqlClient.request(query, {
    version,
    filters,
    pagination,
    ...(sort ? { sort } : {}),
    ...(hasPriority ? { priority_pks: priorityPks } : {}),
    ...statusPks
  });
  if (!status) return send({});
  const sendWithStatus = async (statusPks, attempt) => {
    const data = await send(statusPks);
    const latestPks = readStatusPks(schemaType, status.specKey);
    if (attempt >= MAX_STATUS_ATTEMPTS || isEqual(latestPks, statusPks)) return data;
    return sendWithStatus(latestPks, attempt + 1);
  };
  return sendWithStatus(readStatusPks(schemaType, status.specKey), 1);
};

const exploreDataQueryOptions = (
  scope,
  { schemaType, version, fields, filters, pagination, sort = null, priorityPks = null, status = null }
) => {
  const hasPriority = Boolean(priorityPks?.length);
  return {
    queryKey: [
      FETCH_QUERY_KEYS.EXPLORE_DATA,
      ...scope,
      schemaType,
      version,
      filters,
      pagination,
      fields,
      sort,
      hasPriority ? priorityPks : null,
      status
    ],
    queryFn: async () => await requestExploreData({ schemaType, version, fields, filters, pagination, sort, priorityPks, status }),
    meta: { exploreRequest: { schemaType, version, fields, status } },
    staleTime: CACHE_DURATION.ONE_MINUTE,
    refetchOnWindowFocus: false
  };
};

// Strawberry camelizes GraphQL type names while query fields stay snake_case,
// so introspection must ask for the camelized type name (e.g. "siteInformation").
export const useSchemaIntrospection = (schemaType) => {
  const scope = useExploreScope();
  return useQuery({
    ...introspectionQueryOptions(scope, schemaType),
    enabled: Boolean(schemaType)
  });
};

export const useSchemaVersions = (schemaType) => {
  const scope = useExploreScope();
  return useQuery({
    ...versionsQueryOptions(scope, schemaType),
    enabled: Boolean(schemaType)
  });
};

export const useExploreData = ({ schemaType, version, fields, filters, pagination, sort = null, priorityPks = null, status = null, enabled = true }) => {
  const scope = useExploreScope();
  const isEnabled = Boolean(enabled && schemaType && version && fields?.length);
  return useQuery({
    ...exploreDataQueryOptions(scope, { schemaType, version, fields, filters, pagination, sort, priorityPks, status }),
    // Keep the previous rows on screen only while a replacement is being fetched.
    // With nothing to fetch (e.g. no published version after leaving edit mode)
    // the table must empty out instead of lingering on the draft rows.
    placeholderData: isEnabled ? keepPreviousData : undefined,
    enabled: isEnabled
  });
};

// Imperative variants for code that runs outside the render cycle (bulk
// download/upload ref resolution): served from the cache while fresh, fetched
// otherwise.
export const fetchSchemaIntrospection = (schemaType) =>
  queryClient.fetchQuery(introspectionQueryOptions(getExploreScope(), schemaType));

export const fetchSchemaVersions = (schemaType) =>
  queryClient.fetchQuery(versionsQueryOptions(getExploreScope(), schemaType));

export const fetchExploreData = (params) =>
  queryClient.fetchQuery(exploreDataQueryOptions(getExploreScope(), params));

export const fetchExploreRowsByIds = async ({ schemaType, version, fields, idFieldName, ids }) => {
  const query = buildExploreQuery({ schemaType, fields });
  const results = await Promise.all(
    ids.map((id) =>
      gqlClient.request(query, {
        version,
        filters: [{ field: idFieldName, op: KNOWLEDGE_FILTER_OPERATORS.IS.op, value: id }],
        pagination: { offset: 0, limit: 1 }
      })
    )
  );
  return results.flatMap((data) => data?.[schemaType]?.items ?? []);
};

// Lazily fetches the distinct values of a [String] column for its multi-select
// picker. Cached per (scope, schemaType, field, version); only runs once `enabled`.
export const useExploreFieldValues = ({ schemaType, field, version, enabled }) => {
  const scope = useExploreScope();
  return useQuery({
    queryKey: [FETCH_QUERY_KEYS.EXPLORE_FIELD_VALUES, ...scope, schemaType, field, version],
    queryFn: async () => await gqlClient.request(buildFieldValuesQuery(schemaType), { field, version }),
    enabled: Boolean(enabled && schemaType && field && version),
    staleTime: CACHE_DURATION.ONE_MINUTE,
    refetchOnWindowFocus: false
  });
};

// Still refetched when rows stay pinned: this moves the tab onto a newly created draft.
const invalidateExploreVersionQueries = (schemaType) => {
  queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.EXPLORE_VERSIONS, ...getExploreScope(), schemaType] });
  queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.DRAFT] });
};

const invalidateExploreQueries = (schemaType) => {
  queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.EXPLORE_DATA, ...getExploreScope(), schemaType] });
  invalidateExploreVersionQueries(schemaType);
};

const patchItems = (data, schemaType, idFieldName, freshById) => {
  const result = data?.[schemaType];
  if (!result) return data;
  const items = result.items.map((item) => freshById.get(item[idFieldName]) ?? item);
  return { ...data, [schemaType]: { ...result, items } };
};

// The pk filter is case-insensitive, so ask for the server's page cap, not just the wanted count.
const REFRESH_ROWS_LIMIT = 500;

const refreshQueryRows = async (query, ids) => {
  const { schemaType, version, fields, status } = query.meta.exploreRequest;
  const idField = fields.find(isIdField);
  const shownIds = new Set((query.state.data?.[schemaType]?.items ?? []).map((item) => item[idField?.name]));
  const wanted = idField ? ids.filter((id) => shownIds.has(id)) : [];
  if (wanted.length === 0) return;
  try {
    const data = await requestExploreData({
      schemaType,
      version,
      fields,
      status,
      filters: [{ field: idField.name, op: KNOWLEDGE_FILTER_OPERATORS.IS_ANY_OF.op, value: wanted }],
      pagination: { offset: 0, limit: REFRESH_ROWS_LIMIT }
    });
    const freshById = new Map((data?.[schemaType]?.items ?? []).map((item) => [item[idField.name], item]));
    queryClient.setQueryData(query.queryKey, (current) => patchItems(current, schemaType, idField.name, freshById));
  } catch (error) {
    console.error("Failed to refresh Explore rows in place, refetching the page", error);
    queryClient.invalidateQueries({ queryKey: query.queryKey, exact: true });
  }
};

const refreshingRows = new Map();

// Patches rows where they stand; other cached pages are only marked stale. A row already being
// read is skipped, except after a write (`afterWrite`): the read in flight may predate it, so the
// row is read once more when it lands.
export const refreshExploreRowsInPlace = async ({ schemaType, version, ids, afterWrite = false }) => {
  if (!version) return;
  const scope = getExploreScope();
  const rowKey = (id) => JSON.stringify([...scope, schemaType, version, id]);
  const freshIds = ids.filter((id) => {
    const inFlight = refreshingRows.get(rowKey(id));
    if (inFlight && afterWrite) inFlight.isStale = true;
    return !inFlight;
  });
  if (freshIds.length === 0) return;
  freshIds.forEach((id) => refreshingRows.set(rowKey(id), { isStale: false }));
  let staleIds = [];
  try {
    const queries = queryClient.getQueryCache().findAll({
      queryKey: [FETCH_QUERY_KEYS.EXPLORE_DATA, ...scope, schemaType, version]
    });
    await Promise.all(queries.filter((query) => query.meta?.exploreRequest).map((query) => refreshQueryRows(query, freshIds)));
    queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.EXPLORE_DATA, ...scope, schemaType], refetchType: "none" });
  } finally {
    staleIds = freshIds.filter((id) => refreshingRows.get(rowKey(id))?.isStale);
    freshIds.forEach((id) => refreshingRows.delete(rowKey(id)));
  }
  if (staleIds.length > 0) await refreshExploreRowsInPlace({ schemaType, version, ids: staleIds });
};

// Called after a draft is published or discarded: the explore tab's default version
// follows the agent's draft/active knowledge maps, so those must refresh as well.
export const invalidateAllExploreQueries = () => {
  queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.EXPLORE_DATA] });
  queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.EXPLORE_VERSIONS] });
  queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.EXPLORE_FIELD_VALUES] });
  queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.EXPLORE_REF_RESOLVERS] });
  queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.ASSISTANT_KNOWLEDGE] });
};

const CONFIGURATION_VALIDATIONS_REFETCH_DELAY_MS = 300;
export const scheduleConfigurationValidationsRefetch = debounce(
  () => useAppStore.getState().refetchValidationResults(),
  CONFIGURATION_VALIDATIONS_REFETCH_DELAY_MS
);

const applyMutationValidations = (data, options) => {
  useAppStore.getState().applyKnowledgeValidationScopes(extractMutationValidationScopes(data), options);
  if (extractMutationAffectsConfigurationValidations(data)) scheduleConfigurationValidationsRefetch();
};

const mutationOnSuccess = (schemaType, getOptions) => (data, variables) => {
  applyMutationValidations(data, getOptions?.(variables));
  invalidateExploreQueries(schemaType);
};

export const useCreateRow = (schemaType) => useMutation({
  mutationFn: async ({ version, input }) =>
    await gqlClient.request(buildCreateMutation(schemaType), { version, input }),
  onSuccess: mutationOnSuccess(schemaType)
});

export const useUpdateRow = (schemaType) => useMutation({
  mutationFn: async ({ version, id, input }) =>
    await gqlClient.request(buildUpdateMutation(schemaType), { version, id, input }),
  onSuccess: (data, variables) => {
    if (!variables.pinRow) {
      mutationOnSuccess(schemaType)(data, variables);
      return;
    }
    applyMutationValidations(data);
    refreshExploreRowsInPlace({ schemaType, version: variables.version, ids: [variables.id], afterWrite: true });
    invalidateExploreVersionQueries(schemaType);
  }
});

export const useDeleteRow = (schemaType) => useMutation({
  mutationFn: async ({ version, id }) =>
    await gqlClient.request(buildDeleteMutation(schemaType), { version, id }),
  onSuccess: mutationOnSuccess(schemaType)
});

export const useBulkDeleteRows = (schemaType) => useMutation({
  mutationFn: async ({ version, ids }) =>
    await gqlClient.request(buildBulkDeleteMutation(schemaType), { version, ids }),
  onSuccess: mutationOnSuccess(schemaType)
});

export const useBulkUploadRows = (schemaType) => useMutation({
  mutationFn: async ({ version, inputs, replace }) =>
    await gqlClient.request(
      replace ? buildBulkReplaceMutation(schemaType) : buildBulkUpsertMutation(schemaType),
      { version, inputs }
    ),
  onSuccess: mutationOnSuccess(schemaType, ({ replace }) => ({ authoritative: Boolean(replace) }))
});
